import type { GraphQLResult } from '#src/components/form/Form/index.tsx';
import { TwoFADialog } from '#src/components/form/TwoFADialog/index.tsx';
import type {
  Prompt2faCallback,
  SignedInInfo,
} from '#src/contexts/connection/index.tsx';
import { useConnectionContext } from '#src/contexts/connection/index.tsx';
import { useSignInMutation } from '#src/contexts/currentUser/useSignIn.ts';

type Props = {
  open: boolean;
  otpSessionChallenge: string;
  onSignedIn: Prompt2faCallback | null;
  onClose: () => void;
  reason?: string;
};

export const PromptTwoFactAuthDialog = ({
  open,
  otpSessionChallenge,
  onSignedIn,
  onClose,
  reason,
}: Props) => {
  const signIn = useSignInMutation();
  const { promptTerms } = useConnectionContext();

  const doSignIn = async (
    values: any,
    onResult: (result: GraphQLResult) => void
  ) => {
    const { otpAttempt } = values;

    const response = await signIn({ otpAttempt, otpSessionChallenge });
    if (response) {
      onResult(response);
    }
  };

  const onSuccess = (result: GraphQLResult) => {
    const { currentUser } = result as GraphQLResult<{
      currentUser: SignedInInfo;
    }>;
    if (onSignedIn) onSignedIn.resolve(currentUser);
  };

  const onCancel = () => {
    if (onSignedIn) onSignedIn.reject('No 2FA');
  };

  const onError = (result: GraphQLResult & { tcuToken?: string }) => {
    if (
      result?.errors
        ?.map(({ message }) => message)
        .includes('must_accept_tcus') ||
      result?.error === 'must_accept_tcus'
    ) {
      onClose();
      if (onSignedIn) onSignedIn.reject('has_accepted_tcus_after_2FA');
      promptTerms({
        closable: false,
        tcuToken: result.tcuToken,
      });
    }
  };

  return (
    <TwoFADialog
      open={open}
      reason={reason}
      onSubmit={doSignIn}
      onSuccess={onSuccess}
      onClose={onClose}
      onError={onError}
      onCancel={onCancel}
    />
  );
};
